import { InfoCircleOutlined } from "@ant-design/icons";
import type { SchemaAttribute } from "@p0security/shared/types/schema";
import { Descriptions, Grid, List, Spacing, Typography } from "@p0security/ui";
import { GraphTooltip } from "components/GraphTooltip";
import {
  Dictionary,
  capitalize,
  isEmpty,
  isNil,
  kebabCase,
  keys,
  omit,
  sortBy,
  upperFirst,
} from "lodash";
import { useMemo } from "react";

import { Timestamp } from "./Timestamp";

type PropertyDatum = { key: string; label?: string; value: any; depth: number };

const { Item } = Descriptions;
const { Text } = Typography;

const innerLabelStyle = {
  fontWeight: "normal",
  fontStyle: "italic",
  fontSize: "small",
};

const autoLabel = (key: string) =>
  capitalize(kebabCase(key).replaceAll("-", " "));

// flattens node data
function* generatePropertyData<LabelKey extends string>(data: {
  value: Dictionary<any>;
  schema?: Dictionary<SchemaAttribute<LabelKey>>;
  depth?: number;
  inlineObjects?: boolean;
}): Generator<PropertyDatum> {
  const { value, schema, depth: inputDepth, inlineObjects } = data;
  const depth = inputDepth ?? 0;

  for (const key of keys(schema ?? omit(value, "key")).sort()) {
    const current = value[key];
    // Only nonempty objects are flattened into rows of their own: recursing
    // into null would throw, an empty object would leave a blank header row
    // with nothing under it, and an inlined object is drawn in its own row.
    if (
      typeof current !== "object" ||
      current === null ||
      isEmpty(current) ||
      Array.isArray(current) ||
      inlineObjects
    ) {
      yield { key, value: current, depth };
      continue;
    }
    // handle nested objects recursively
    yield { key, value: "", depth };
    for (const result of generatePropertyData({
      value: current,
      depth: depth + 1,
    })) {
      const inner = autoLabel(result.key);
      yield { ...result, key: `${key}.${result.key}`, label: inner };
    }
  }

  // Always put 'key' at end
  if ("key" in value) {
    yield { key: "key", value: value.key, depth };
  }
}

// Shared by table cells, list items and an object's fields, so a missing or
// empty value reads the same wherever it appears
const renderValue = (value: unknown, indent?: number): React.ReactNode =>
  isNil(value) ? (
    <Text type="secondary">(No value)</Text>
  ) : Array.isArray(value) ? (
    value.length ? (
      <List dataSource={value} renderItem={renderArrayItem} />
    ) : (
      <Text type="secondary">(No data)</Text>
    )
  ) : typeof value === "object" ? (
    isEmpty(value) ? (
      <Text type="secondary">(No data)</Text>
    ) : (
      <Spacing asChild ml={indent}>
        <div>{renderItemEntries(value)}</div>
      </Spacing>
    )
  ) : (
    <Text copyable>{String(value)}</Text>
  );

// Keys are sorted to match the order of the rows `generatePropertyData` yields
const renderItemEntries = (item: object): React.ReactNode =>
  sortBy(Object.entries(item), ([k]) => k).map(([k, v]) => (
    <div key={k}>
      <Text type="secondary">{autoLabel(k)}: </Text>
      {renderValue(v, 16)}
    </div>
  ));

const renderArrayItem = (item: unknown) => (
  <List.Item>{renderValue(item)}</List.Item>
);

const ObjectDescriptions: React.FC<
  React.PropsWithChildren<{ theme?: "normal" | "tight" }>
> = ({ children, theme }) => {
  const { md } = Grid.useBreakpoint();
  const padding = theme === "tight" ? "8px" : "12px";
  const fontSize = theme === "tight" ? "small" : undefined;

  return (
    <Descriptions
      bordered
      column={1}
      styles={{
        // Note that antd adds extra padding when layout is vertical :(
        content: { fontSize, overflowX: "clip", padding: md ? padding : "0px" },
        label: {
          fontSize,
          fontWeight: 700,
          padding: md ? padding : "0px",
          // Fits "Is provider managed ⓘ"
          width: md ? "165px" : undefined,
        },
      }}
      layout={md ? "horizontal" : "vertical"}
      size="small"
    >
      {children}
    </Descriptions>
  );
};

/** Renders `value` as a bordered table of label/value rows, labeled from
 * `schema`.
 *
 * By default a nested object becomes a header row with an empty value,
 * followed by one italic row per field. With `inlineObjects`, it becomes a
 * single row listing its fields in the value cell instead.
 */
export function Properties<LabelKey extends string>({
  inlineObjects,
  value,
  labelKey,
  schema,
  showKeys,
}: {
  inlineObjects?: boolean;
  value: object | undefined;
  labelKey: LabelKey;
  schema: Dictionary<SchemaAttribute<LabelKey>>;
  showKeys?: boolean;
}): React.ReactNode {
  const items = useMemo(() => {
    if (!value) return [];

    const keyValueData = [
      ...generatePropertyData({ inlineObjects, schema, value }),
    ];
    return keyValueData.map((data) => {
      const { value } = data;

      // schemaItem may be undefined if key is not in schema (e.g. a key with depth > 0)
      const schemaItem = schema ? schema[data.key] : undefined;
      const labeler = schemaItem?.label;
      const label =
        typeof labeler === "string"
          ? labeler
          : (labeler?.[labelKey] ?? data.label ?? data.key);

      const help = data.depth === 0 ? schemaItem?.help : undefined;

      const propLabel = (
        <>
          <div>
            {upperFirst(label)}
            {help && (
              <>
                &nbsp;
                <GraphTooltip title={help}>
                  <InfoCircleOutlined className="cursor-help text-[var(--ant-color-text-secondary)]" />
                </GraphTooltip>
              </>
            )}
          </div>
          {label && showKeys && (
            <div className="font-normal whitespace-nowrap">
              <Text type="secondary">{data.key}</Text>
            </div>
          )}
        </>
      );

      return (
        <Item
          key={data.key}
          label={propLabel}
          styles={{
            label: data.depth > 0 ? innerLabelStyle : undefined,
          }}
        >
          {schemaItem?.type === "date" && typeof value === "number" ? (
            <Timestamp at={value} copyable />
          ) : value === "" ? null : (
            renderValue(value)
          )}
        </Item>
      );
    });
  }, [inlineObjects, labelKey, schema, showKeys, value]);

  return (
    <div className="[&_td:not(:hover)_button.ant-typography-copy]:invisible">
      <ObjectDescriptions theme="tight">{items}</ObjectDescriptions>
    </div>
  );
}
